import Cookies from 'js-cookie';
import { environment } from "@/env/env.prod";
import { getHeaders } from '@/api/requestHeaders';

const BASE = `${environment.BASE_URL}/user`;

export type Designation = 'Students' | 'Teachers';

export interface SearchedUser {
    employee_id: string;
    name: string;
    email: string;
    role: string;              // student | faculty | admin | subadmin | superadmin | librarian …
    designation: Designation;
    onboarded: boolean;
    is_verified: boolean;
    email_status: 'delivered' | 'bounced' | 'complained' | null;
    profile_pic: string | null;
    matchedOn: Array<'id' | 'name' | 'email'>;
}

export interface SearchResult { users: SearchedUser[]; truncated: boolean; }
export type SearchField = 'any' | 'id' | 'name' | 'email';
export type SearchScope = 'all' | 'students' | 'teachers' | 'faculty' | 'admin' | 'subadmin' | 'superadmin' | 'librarian';

// An API failure that keeps what the server said (status, machine code, suggested fix).
export class ApiError extends Error {
    status: number;
    code?: string;
    suggestion?: string | null;
    constructor(message: string, status: number, code?: string, suggestion?: string | null) {
        super(message);
        this.status = status;
        this.code = code;
        this.suggestion = suggestion;
    }
}

export class UserService {
    // Read live, not captured in the constructor: `userService` below is a
    // module singleton, so a snapshot taken at import time outlives every
    // token refresh and account switch in the tab. That is what sent an
    // expired token (and the previous institute) to /user/addUser.
    get headers() { return getHeaders(); }

    private async post(route: string, body: object) {
        const res = await fetch(`${BASE}/${route}`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(body),
        });
        let data: any = null;
        try { data = await res.json(); } catch { /* non-JSON error body */ }
        if (!res.ok) {
            // addUser answers { error }, the newer routes answer { message }
            const message = data?.message || data?.error || 'Something went wrong';
            throw new ApiError(message, res.status, data?.code, data?.suggestion);
        }
        return data;
    }

    async addUser(userId: string, email: string, operationEntity: number) {
        try {
            return await this.post('addUser', { userId, email, operationEntity });
        } catch (e) {
            if (e instanceof ApiError && e.suggestion) {
                throw new Error(`${e.message}. Did you mean ${e.suggestion}?`);
            }
            throw e;
        }
    }

    /** One box over ids, names and emails. Admin / subadmin / superadmin only (the server enforces it). */
    async searchUsers(q: string, scope: SearchScope = 'all', limit = 25, field: SearchField = 'any'): Promise<SearchResult> {
        const data = await this.post('searchUsers', { q, scope, limit, field });
        return { users: data.users || [], truncated: !!data.truncated };
    }

    /**
     * Fix the email and/or id of a user who has not signed up yet. The server re-checks
     * that the user is still not onboarded. Changing the id re-sends the onboarding email
     * automatically (the old email names the old id); an email-only change sends it only
     * when sendOnboarding is true.
     */
    async updateUserContact(p: { designation: Designation; userId: string; newEmail?: string; newUserId?: string; sendOnboarding?: boolean }) {
        return await this.post('updateUserContact', p) as {
            success: boolean; userId: string; email: string;
            emailChanged: boolean; idChanged: boolean;
            onboardingEmail: 'sent' | 'failed' | 'not_requested' | 'sent_automatically';
        };
    }

    async resendOnboarding(designation: Designation, userId: string) {
        return await this.post('resendOnboarding', { designation, userId }) as { success: boolean; message: string; email: string };
    }
}

export const userService = new UserService();
